'use client'

import { useState, useRef, useEffect } from 'react'
import { Globe, Check } from 'lucide-react'
import { useClientTranslation } from '@/hooks/useClientTranslation'

interface LanguageSelectorProps {
  onChange?: (language: string) => void
  className?: string
  iconOnly?: boolean
}

export function LanguageSelector({ onChange, className = '', iconOnly = false }: LanguageSelectorProps) {
  const { t, i18n } = useClientTranslation('common')
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const languages = [
    { code: 'zh-TW', label: t('language.zh-TW') },
    { code: 'zh-CN', label: t('language.zh-CN') },
    { code: 'en', label: t('language.en') }
  ]

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language)
    localStorage.setItem('i18nextLng', language)
    localStorage.setItem('language', language)
    setIsOpen(false)
    onChange?.(language)
  }

  const currentLanguage = languages.find(lang => lang.code === i18n.language)

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center rounded-lg transition-all ${
          iconOnly 
            ? 'p-2 bg-white/30 hover:bg-white/50 backdrop-blur-sm border border-white/20' 
            : 'space-x-2 px-3 py-2 text-sm bg-white hover:bg-gray-50 shadow-sm hover:shadow-md border border-gray-200'
        }`}
        title={iconOnly ? currentLanguage?.label : undefined}
      >
        <Globe className={`${iconOnly ? 'h-5 w-5 text-gray-700' : 'h-4 w-4 text-gray-600'}`} />
        {!iconOnly && (
          <>
            <span className="text-gray-700 font-medium">{currentLanguage?.label}</span>
            <svg
              className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center justify-between"
            >
              <span className={`${i18n.language === language.code ? 'text-blue-600 font-medium' : 'text-gray-700'}`}>
                {language.label}
              </span>
              {i18n.language === language.code && (
                <Check className="h-4 w-4 text-blue-600" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}